import { z } from "zod/v4";
import type { components } from "./generated/companion-api";
import { CompanionError, companionRequest } from "../companion/transport";
export {
  CompanionError,
  getCompanionSettings,
  saveCompanionSettings,
} from "../companion/transport";
export type { CompanionErrorCode, CompanionSettings } from "../companion/transport";

type CompanionErrorResponse = components["schemas"]["ErrorBody"];
type CompanionHealthResponse = components["schemas"]["HealthResponse"];
type CompanionTerminalStatusResponse = components["schemas"]["PaymentTerminalResponse"];
type CompanionTestResponse = components["schemas"]["PaymentTerminalTestResponse"];
type CompanionStartPaymentRequest = components["schemas"]["StartPaymentBody"];
type CompanionStartPaymentResponse = components["schemas"]["StartPaymentResponse"];
type CompanionPaymentStatusResponse = components["schemas"]["PaymentStatusResponse"];

const companionErrorResponseSchema: z.ZodType<CompanionErrorResponse> = z.object({
  error: z.string(),
  active_payment_id: z.string().nullable().optional(),
});

const healthResponseSchema: z.ZodType<CompanionHealthResponse> = z.object({
  status: z.string(),
  version: z.string(),
  paymentModule: z.boolean(),
});

const terminalStatusSchema: z.ZodType<CompanionTerminalStatusResponse> = z.object({
  configured: z.boolean(),
  reachable: z.boolean(),
  host: z.string(),
  port: z.number(),
  protocol: z.string(),
  protocolVersion: z.string(),
});

const testResponseSchema: z.ZodType<CompanionTestResponse> = z.object({
  success: z.boolean(),
  protocolVersion: z.string(),
});

const startPaymentResponseSchema: z.ZodType<CompanionStartPaymentResponse> = z.object({
  paymentId: z.string(),
  status: z.string(),
});

export const companionPaymentStatusSchema = z.enum([
  "pending",
  "connecting",
  "request_sent",
  "approved",
  "not_completed",
  "unknown",
]);

const paymentStatusResponseSchema: z.ZodType<CompanionPaymentStatusResponse> = z.object({
  paymentId: z.string(),
  status: companionPaymentStatusSchema,
  amountCents: z.number(),
  currency: z.string(),
  authorization: z.string().nullable().optional(),
  resultCode: z.string().nullable().optional(),
  resultDetail: z.string().nullable().optional(),
});

function mapErrorStatus(status: number, error: string | undefined) {
  if (status === 401 || status === 403) {
    return "not_paired" as const;
  }
  if (status === 409) {
    return "terminal_busy" as const;
  }
  if (status === 503 && error === "terminal_not_configured") {
    return "terminal_not_configured" as const;
  }
  if (status === 502) {
    return "terminal_unreachable" as const;
  }
  if (status === 400) {
    return "invalid_request" as const;
  }
  if (status === 404) {
    return "not_found" as const;
  }
  return "unknown" as const;
}

async function companionFetch(path: string, init?: RequestInit) {
  const response = await companionRequest(path, init);

  if (!response.ok) {
    let errorCode: string | undefined;
    let activePaymentId: string | undefined;
    try {
      const parsed = companionErrorResponseSchema.safeParse(await response.json());
      if (parsed.success) {
        errorCode = parsed.data.error;
        activePaymentId = parsed.data.active_payment_id ?? undefined;
      }
    } catch {
      // non-JSON error body
    }
    const code = mapErrorStatus(response.status, errorCode);
    const message =
      code === "terminal_busy"
        ? "Le terminal est déjà occupé par un autre paiement."
        : code === "not_paired"
          ? "Companion non appairé : vérifiez le port et le token d'appairage."
          : code === "terminal_not_configured"
            ? "Le TPE n'est pas configuré dans le companion."
            : code === "terminal_unreachable"
              ? "Le terminal est injoignable sur le réseau."
              : code === "invalid_request"
                ? "Requête de paiement invalide."
                : code === "not_found"
                  ? "Paiement introuvable."
                  : "Erreur inattendue du companion.";
    throw new CompanionError(code, message, activePaymentId);
  }

  return response;
}

export async function companionHealth() {
  const response = await companionFetch("/v1/health");
  return healthResponseSchema.parse(await response.json());
}

export async function companionTerminalStatus() {
  const response = await companionFetch("/v1/payment-terminal");
  return terminalStatusSchema.parse(await response.json());
}

export async function companionTestTerminal() {
  const response = await companionFetch("/v1/payment-terminal/test", {
    method: "POST",
  });
  return testResponseSchema.parse(await response.json());
}

export async function companionStartPayment(options: CompanionStartPaymentRequest) {
  const response = await companionFetch("/v1/payments", {
    method: "POST",
    body: JSON.stringify(options),
  });
  return startPaymentResponseSchema.parse(await response.json());
}

export async function companionPaymentStatus(paymentId: string) {
  const response = await companionFetch(`/v1/payments/${encodeURIComponent(paymentId)}`);
  return paymentStatusResponseSchema.parse(await response.json());
}
